import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { scrollToId } from "./Reveal";

const LINES = ["Turn Your Destination", "Supply Into a Scalable", "Travel Business"];

const MaskedLine = ({ text, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
      className="block"
    >
      {text}
    </motion.span>
  </span>
);

const LAYERS = [
  { id: "supply", label: "DMC Supply", chips: ["Hotels", "Activities", "Transfers", "Packages", "Pricing"], style: "border-sand bg-white" },
  { id: "engine", label: "Travel-Tek", chips: ["Catalogue", "Package Builder", "Quotation Engine", "Inquiry Management", "Distribution"], style: "border-ink bg-ink text-paper", dark: true },
  { id: "channels", label: "Sales Channels", chips: ["Branded Website", "Travel Agents", "B2B Partners", "Direct Travellers"], style: "border-sand bg-white" },
  { id: "business", label: "Business", chips: ["Enquiries", "Quotes", "Bookings", "Customers", "Repeat Business"], style: "border-terra/50 bg-terra/5" },
];

const TICKER = [
  "Supplier rate added to catalogue",
  "Package built from live inventory",
  "Branded storefront published",
  "Agent requests a quotation",
  "Traveller sends an enquiry",
  "Quote sent · customer data saved",
];

export default function CommerceHero() {
  const [ti, setTi] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTi((i) => (i + 1) % TICKER.length), 1800);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="top" className="relative overflow-hidden pb-20 pt-32 sm:pt-40 lg:pb-24">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(#E6DFD0 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          maskImage: "linear-gradient(to bottom, black 30%, transparent 85%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 30%, transparent 85%)",
        }}
      />
      <div className="container-x relative grid grid-cols-1 items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mb-7 inline-flex max-w-full items-center gap-3 rounded-full border border-sand bg-white/70 px-4 py-2"
            data-testid="hero-positioning-badge"
          >
            <span className="h-2 w-2 shrink-0 rounded-full bg-terra" />
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-ink">
              The commerce platform for destination businesses
            </span>
          </motion.div>

          <h1 className="font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-[3.9rem]">
            {LINES.map((l, i) => (
              <MaskedLine key={l} text={l} delay={0.35 + i * 0.14} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-stone2 sm:text-lg"
          >
            Travel-Tek gives DMCs and destination businesses one platform to manage
            destination products, build packages, create quotations, launch branded
            digital storefronts, capture enquiries, and sell directly to travel
            agents and travellers.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={scrollToId("#demo")}
              data-testid="hero-request-demo-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-terra hover:text-ink"
            >
              Book a Demo
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              href="/how-it-works"
              data-testid="hero-explore-demo-btn"
              className="group inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-ink hover:bg-ink hover:text-paper"
            >
              See How It Works
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-8 font-mono text-xs uppercase tracking-[0.18em] text-stone2"
            data-testid="hero-tertiary"
          >
            Built for DMCs. White-labelled for your brand.
          </motion.p>
        </div>

        <div className="lg:col-span-6" data-testid="hero-commerce-visual">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-lg"
          >
            <div aria-hidden className="absolute bottom-6 left-6 top-6 w-px bg-gradient-to-b from-transparent via-terra/50 to-transparent" />
            <div aria-hidden className="absolute bottom-6 left-6 top-6 w-px">
              <span className="flow-dot absolute -left-[3px] h-1.5 w-1.5 rounded-full bg-terra" />
              <span className="flow-dot absolute -left-[3px] h-1.5 w-1.5 rounded-full bg-terra" style={{ animationDelay: "1.2s" }} />
            </div>
            <div className="space-y-3 pl-10">
              {LAYERS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.75 + i * 0.16, duration: 0.6 }}
                  data-testid={`hero-layer-${l.id}`}
                  className={`rounded-2xl border p-4 shadow-[0_20px_45px_-25px_rgba(11,31,51,0.3)] ${l.style}`}
                >
                  <p className={`mb-2 font-mono text-[9px] font-bold uppercase tracking-[0.22em] ${l.dark ? "text-terra" : "text-stone2"}`}>
                    {String(i + 1).padStart(2, "0")} · {l.label}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {l.chips.map((c) => (
                      <span key={c} className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${l.dark ? "bg-white/10 text-paper/85" : "bg-paper text-ink"}`}>
                        {c}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="ml-10 mt-4 flex items-center gap-2.5 rounded-full border border-sand bg-white px-5 py-2.5" data-testid="hero-flow-ticker">
              <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-terra" />
              <AnimatePresence mode="wait">
                <motion.span
                  key={ti}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35 }}
                  className="truncate font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-ink"
                >
                  {TICKER[ti]}
                </motion.span>
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
